import React, { useMemo, useState } from 'react';
import { CalendarDays, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'date-fns/locale';

const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const normalizeDates = (items) => {
  return (items || [])
    .map(d => String(d || '').trim())
    .filter(Boolean)
    .map(d => d.includes('T') ? d.slice(0, 10) : d)
    .filter(d => !Number.isNaN(new Date(`${d}T12:00:00`).getTime()))
    .sort();
};

const parseDatesFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('dates') || params.get('selectedDates') || params.get('date') || '';

  if (raw) {
    const parsed = normalizeDates(raw.split(','));
    localStorage.setItem('ucan_service_dates', JSON.stringify(parsed));
    return parsed;
  }

  try {
    return normalizeDates(JSON.parse(localStorage.getItem('ucan_service_dates') || '[]'));
  } catch {
    return [];
  }
};

const formatDate = (dateString) => {
  return new Date(`${dateString}T12:00:00`).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export default function ServiceDatesSelector() {
  const initialDates = useMemo(() => parseDatesFromUrl(), []);
  const [dates, setDates] = useState(initialDates);

  const selectedDates = dates.map(d => new Date(`${d}T12:00:00`));

  const handleSelectDates = (value) => {
    const nextDates = (value || [])
      .map(toLocalYMD)
      .sort();

    setDates(nextDates);
    localStorage.setItem('ucan_service_dates', JSON.stringify(nextDates));
  };

  const removeDate = (date) => {
    setDates(prev => {
      const nextDates = prev.filter(d => d !== date);
      localStorage.setItem('ucan_service_dates', JSON.stringify(nextDates));
      return nextDates;
    });
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="service-dates-selector">
      <div className="flex items-center gap-2 mb-2">
        <CalendarDays className="w-5 h-5 text-[#E6202E]" />
        <h3 className="font-bold text-lg">Fechas del servicio</h3>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Elige los días que necesitas. Si ya los seleccionaste antes, aparecerán marcados aquí.
      </p>

      <div className="overflow-x-auto">
        <Calendar
          mode="multiple"
          selected={selectedDates}
          onSelect={handleSelectDates}
          locale={es}
          numberOfMonths={1}
          disabled={{ before: new Date() }}
        />
      </div>
      {dates.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-4">
          {dates.map(date => (
            <button
              key={date}
              type="button"
              onClick={() => removeDate(date)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-[#E6202E] text-xs font-semibold border border-red-100"
            >
              {formatDate(date)}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400 mt-3">
          Aún no hay fechas seleccionadas.
        </p>
      )}
    </div>
  );
}
